// The quick add of redesign D, one component for both stores (Oct 24; loaded only where settings.redesign is on, see
// layout/theme.liquid; each brand's look is its tokens in assets/hn-redesign.css / ch-redesign.css over quick-pick.css).
// Every card's Add (snippets/card-product.liquid: data-qp = the handle, data-qp-data = the color dots and, on a
// Hineni cap card, the design's other caps) opens one panel: a bottom sheet under 900 px (drag it down to close), a
// popover anchored to the button above that. Nothing is added without a color and a size: the button reads "Choose a
// size" until one is picked, then "Add to cart · $X" with that size's own price. The variants come from
// /products/<handle>.js, fetched when the pointer rests on a card or a finger touches Add, so the panel usually opens
// filled. Adding goes through /cart/add.js the way Dawn's product-form.js does (the drawer's sections rendered in the
// same request), then the panel closes and the cart drawer opens with its free-shipping and order-by notes.
// Motion (.impeccable/mocks/directions-r2.md "Quick add"): the sheet rises on the drawer curve in 420 ms and leaves in
// 240 ms; the popover scales from 0.96 at the button in 180 ms; reduced motion keeps only the fades (quick-pick.css).
// #qa=<handle>[&size=M] opens that card's quick add on load (for checks and screenshots).
(() => {
  if (window.quickPick) return;
  window.quickPick = { open: (b, opts) => open(b, opts), state: () => S, load: (h) => load(h) }; // for checks in the console

  const mqWide = matchMedia('(min-width: 900px)');
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const mqMouse = matchMedia('(hover: hover) and (pointer: fine)');
  const root = window.Shopify?.routes?.root || '/';
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => [...r.querySelectorAll(s)];
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const money = (cents) => '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''); // the theme's price_drop_zero_cents
  const nameOf = (title) => String(title).replace(/\s*\([^)]*\)\s*$/, '');
  const isColor = (n) => /^colou?r$/i.test(n);
  const isSize = (n) => /^size$/i.test(n);
  // The shipping line under the button: Printful's US rate for one item of the kind (snippets/ship-line.liquid has
  // the same table and where it comes from; change both together)
  const SHIP = { 'T-Shirt': 'Shipping $4.95 · free on $75+', 'Long Sleeve Shirt': 'Shipping $4.95 · free on $75+', 'Crewneck Sweatshirt': 'Shipping $8.79 · free on $75+' };
  const shipLine = (type) => SHIP[type] || 'Free US shipping on $75+';

  // ---------- the products: fetched once a page, on intent ----------
  const cache = new Map();
  const load = (handle) => {
    if (!cache.has(handle)) {
      cache.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(normalize)
        .catch((e) => { cache.delete(handle); throw e; }));
    }
    return cache.get(handle);
  };
  // options as [{ name, values }] whichever shape the endpoint gives
  function normalize(p) {
    p.options = (p.options || []).map((o, i) => (typeof o === 'string'
      ? { name: o, values: [...new Set(p.variants.map((v) => v.options[i]))] }
      : { name: o.name, values: o.values }));
    return p;
  }
  const imgOf = (v) => v?.featured_image?.src || v?.featured_media?.preview_image?.src || null;
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const preload = (src) => { if (src) { const i = new Image(); i.src = sized(src, 480); } };

  // ---------- the panel ----------
  let panel, scrim, S = null; // S: the open panel's state
  const ICON_CLOSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const ICON_CHECK = '<svg class="qp__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.5l3.6 3.4L13.5 4.5"/></svg>';

  function build() {
    scrim = document.createElement('div');
    scrim.className = 'qp-scrim';
    scrim.hidden = true;
    panel = document.createElement('div');
    panel.className = 'qp';
    panel.hidden = true;
    panel.tabIndex = -1;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'QpTitle');
    panel.innerHTML = `
      <div class="qp__grab" aria-hidden="true"><span></span></div>
      <button class="qp__close" type="button" aria-label="Close">${ICON_CLOSE}</button>
      <div class="qp__body">
        <div class="qp__top">
          <div class="qp__media"><img alt="" class="qp__img"><img alt="" class="qp__img is-off"></div>
          <div class="qp__head">
            <h2 class="qp__title" id="QpTitle"></h2>
            <p class="qp__he" lang="he" dir="rtl"></p>
            <p class="qp__means"></p>
            <p class="qp__price"></p>
          </div>
        </div>
        <div class="qp__rows"></div>
        <p class="qp__error" role="alert" hidden></p>
        <button class="qp__go" type="button"><span class="qp__labels"><span class="qp__l is-on"></span><span class="qp__l" aria-hidden="true"></span></span></button>
        <p class="qp__ship"></p>
        <a class="qp__more" href="#">See all details</a>
      </div>`;
    document.body.append(scrim, panel);
    qs('.qp__close', panel).addEventListener('click', () => close());
    scrim.addEventListener('click', () => close());
    qs('.qp__go', panel).addEventListener('click', go);
    qs('.qp__rows', panel).addEventListener('change', onPick);
    document.addEventListener('keydown', (e) => {
      if (!S) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Tab') trap(e);
    });
    // the popover closes on a click anywhere else
    document.addEventListener('pointerdown', (e) => {
      if (!S || S.mode !== 'pop' || panel.contains(e.target) || S.opener?.contains(e.target)) return;
      close();
    }, true);
    window.addEventListener('resize', () => {
      if (!S) return;
      if ((S.mode === 'pop') !== mqWide.matches) close(true); else if (S.mode === 'pop') place();
    });
    drag();
  }

  // The button's two labels crossfade, so its width never jumps
  function label(text, html) {
    const ls = qsa('.qp__l', panel), on = ls.find((l) => l.classList.contains('is-on')), off = ls.find((l) => l !== on);
    if (!html && on.textContent === text) return;
    if (html) off.innerHTML = html; else off.textContent = text;
    off.classList.add('is-on'); off.removeAttribute('aria-hidden');
    on.classList.remove('is-on'); on.setAttribute('aria-hidden', 'true');
  }

  function open(opener, opts = {}) {
    if (!panel) build();
    if (S) close(true);
    const card = opener.closest('.card-wrapper, .hn-grid__item, li') || null;
    let data = {};
    try { data = JSON.parse(opener.dataset.qpData || '{}'); } catch {}
    S = { opener, card, data, handle: opener.dataset.qp, mode: mqWide.matches ? 'pop' : 'sheet', sel: [], busy: false, size: opts.size, p: null, keepColor: data.c || null }; // the card's color first
    // what the card already shows, so the panel is never empty while the variants load
    const cardImg = card && qs('.card__media img', card);
    qs('.qp__title', panel).textContent = nameOf(card ? (qs('.card__heading a', card)?.textContent || '').trim() : '') || '';
    qs('.qp__he', panel).textContent = card ? (qs('.hn-card-he', card)?.textContent || '').trim() : '';
    qs('.qp__means', panel).textContent = card ? (qs('.hn-card-means', card)?.textContent || '').trim() : '';
    qs('.qp__price', panel).textContent = '';
    qs('.qp__error', panel).hidden = true;
    qs('.qp__ship', panel).textContent = '';
    qs('.qp__rows', panel).innerHTML = '<p class="qp__loading">Loading colors and sizes…</p>';
    photo(cardImg?.currentSrc || cardImg?.src || '', true);
    const goBtn = qs('.qp__go', panel);
    goBtn.classList.remove('is-added');
    goBtn.disabled = true;
    label('Choose a size');

    panel.classList.toggle('is-sheet', S.mode === 'sheet');
    panel.classList.toggle('is-pop', S.mode === 'pop');
    panel.classList.remove('is-open', 'is-dragging');
    panel.style.transform = panel.style.opacity = '';
    panel.hidden = false;
    if (S.mode === 'sheet') {
      document.documentElement.classList.add('qp-lock');
      scrim.hidden = false;
      void panel.offsetHeight;
      scrim.classList.add('is-open');
    } else {
      place();
      void panel.offsetHeight;
    }
    opener.setAttribute('aria-expanded', 'true');
    // the closed state is laid out (offsetHeight above), so the class change transitions without waiting a frame
    panel.classList.add('is-open');
    panel.focus({ preventScroll: true });

    const mine = S;
    load(S.handle).then((p) => { if (S === mine) fill(p, opts); })
      .catch((err) => { console.error('quick-pick', err); if (S === mine) qs('.qp__rows', panel).innerHTML = `<p class="qp__loading">This didn't load. <a href="${root}products/${esc(S.handle)}">Open the product page</a></p>`; });
    // a cap card's other styles, so their prices are there when the row draws
    (data.caps || []).forEach((c) => load(c.h).catch(() => {}));
  }

  // Draw the rows for a product (and again when a cap style switches the product)
  function fill(p, opts = {}) {
    S.p = p;
    const keep = S.keepColor;
    S.sel = p.options.map((o, i) => {
      if (o.values.length === 1) return o.values[0];
      if (isSize(o.name)) return null;
      if (isColor(o.name) && keep && o.values.includes(keep)) return keep;
      return o.values.find((v) => p.variants.some((x) => x.available && x.options[i] === v)) ?? o.values[0];
    });
    const si = p.options.findIndex((o) => isSize(o.name));
    if (si >= 0 && opts.size && p.options[si].values.includes(opts.size)) S.sel[si] = opts.size;
    qs('.qp__title', panel).textContent = nameOf(p.title);
    qs('.qp__ship', panel).textContent = shipLine(p.type);
    const sw = (S.swatches = S.swatchesFor?.[p.handle] || S.data.sw || {});
    let html = '';
    const caps = S.data.caps || [];
    if (caps.length > 1) {
      html += `<fieldset class="qp__row" data-row="cap"><legend class="qp__legend">Cap style: <b>${esc(caps.find((c) => c.h === p.handle)?.n || '')}</b></legend><div class="qp__pills">${
        caps.map((c) => `<label class="qp-pill"><input type="radio" name="qpo-cap" value="${esc(c.h)}"${c.h === p.handle ? ' checked' : ''}><span>${esc(c.n)}<small class="qp__capprice" data-cap="${esc(c.h)}"></small></span></label>`).join('')
      }</div></fieldset>`;
    }
    p.options.forEach((o, i) => {
      if (o.values.length === 1) return;
      const color = isColor(o.name), size = isSize(o.name);
      html += `<fieldset class="qp__row${size ? ' qp__row--size' : ''}" data-row="${i}"><legend class="qp__legend">${esc(o.name)}${size ? '' : `: <b>${esc(S.sel[i])}</b>`}</legend><div class="${color ? 'qp__swatches' : 'qp__pills'}">${
        o.values.map((v) => {
          const checked = S.sel[i] === v ? ' checked' : '';
          if (color) {
            const bg = sw[v] || '#ffffff';
            return `<label class="qp-sw" title="${esc(v)}"><input type="radio" name="qpo-${i}" value="${esc(v)}"${checked}><span class="qp-sw__dot" style="background:${esc(bg)}"></span><span class="visually-hidden">${esc(v)}</span></label>`;
          }
          return `<label class="qp-pill${size ? ' qp-pill--size' : ''}"><input type="radio" name="qpo-${i}" value="${esc(v)}"${checked}><span>${esc(v)}</span></label>`;
        }).join('')
      }</div></fieldset>`;
    });
    qs('.qp__rows', panel).innerHTML = html;
    capPrices();
    update(true);
    if (S.mode === 'pop') place();
  }

  function capPrices() {
    qsa('.qp__capprice', panel).forEach((el) => {
      load(el.dataset.cap).then((cp) => { el.textContent = ` · ${money(cp.price_min ?? cp.price)}`; }).catch(() => {});
    });
  }

  // the variant the picks point to, or null while one is open (the size)
  const match = (sel = S.sel) => S.p.variants.find((v) => v.options.every((x, i) => sel[i] == null || sel[i] === x));
  const exact = () => (S.sel.every((x) => x != null) ? S.p.variants.find((v) => v.options.every((x, i) => S.sel[i] === x)) : null);

  function update(instant) {
    const p = S.p;
    // each choice: dimmed when nothing in stock carries it with the other picks (a color or style ignores the size
    // picked, which is let go if the new color doesn't come in it); a size that isn't made in these picks is disabled
    p.options.forEach((o, i) => {
      const row = qs(`.qp__row[data-row="${i}"]`, panel);
      if (!row) return;
      qsa('input', row).forEach((inp) => {
        const sel = S.sel.map((x, j) => (!isSize(o.name) && isSize(p.options[j].name) ? null : x));
        sel[i] = inp.value;
        const ok = p.variants.some((v) => v.available && v.options.every((x, j) => sel[j] == null || sel[j] === x));
        inp.closest('label').classList.toggle('is-out', !ok);
        if (isSize(o.name)) inp.disabled = !ok;
      });
      const b = qs('.qp__legend b', row);
      if (b) b.textContent = S.sel[i] ?? '';
    });
    const v = exact(), any = match();
    const shown = v || any;
    const cents = v ? v.price : Math.min(...p.variants.filter((x) => x.options.every((y, i) => S.sel[i] == null || S.sel[i] === y)).map((x) => x.price));
    qs('.qp__price', panel).textContent = Number.isFinite(cents) ? money(cents) : '';
    // the photo of the chosen color (and style)
    const withImg = p.variants.find((x) => imgOf(x) && x.options.every((y, i) => S.sel[i] == null || isSize(p.options[i].name) || S.sel[i] === y));
    photo(imgOf(withImg) || p.featured_image || '', instant);
    // "See all details": the product page on these picks (pick=size: no size chosen yet there either)
    const more = qs('.qp__more', panel);
    const at = shown ? `?variant=${shown.id}${v ? '' : '&pick=size'}` : '';
    more.href = `${root}products/${p.handle}${at}`;
    if (S.busy) return;
    const go = qs('.qp__go', panel);
    go.disabled = false;
    if (!v) label('Choose a size');
    else if (!v.available) { label('Sold out'); go.disabled = true; }
    else label(`Add to cart · ${money(v.price)}`);
  }

  function photo(src, instant) {
    const imgs = qsa('.qp__img', panel), on = imgs.find((i) => !i.classList.contains('is-off')), off = imgs.find((i) => i !== on);
    if (!src) return;
    const url = src.includes('/cdn/') || src.includes('cdn.shopify') ? sized(src.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, ''), 480) : src;
    if (on.getAttribute('src') === url) return;
    if (instant || mqReduce.matches || !on.getAttribute('src')) { on.src = url; off.classList.add('is-off'); return; }
    off.onload = () => { off.classList.remove('is-off'); on.classList.add('is-off'); };
    off.src = url;
  }

  function onPick(e) {
    const inp = e.target;
    if (!S?.p || inp.type !== 'radio') return;
    qs('.qp__error', panel).hidden = true;
    if (inp.name === 'qpo-cap') { // another cap of the design: its own product, keeping the color when it has it
      const ci = S.p.options.findIndex((o) => isColor(o.name));
      S.keepColor = ci >= 0 ? S.sel[ci] : null;
      S.swatchesFor = Object.fromEntries((S.data.caps || []).map((c) => [c.h, c.sw || {}]));
      const mine = S;
      load(inp.value).then((p) => { if (S === mine) fill(p); }).catch(() => {});
      return;
    }
    const i = Number(inp.name.replace('qpo-', ''));
    S.sel[i] = inp.value;
    // a picked size that the new color doesn't come in is let go
    const si = S.p.options.findIndex((o) => isSize(o.name));
    if (si >= 0 && si !== i && S.sel[si] != null && !S.p.variants.some((v) => v.available && v.options.every((x, j) => S.sel[j] === x))) {
      S.sel[si] = null;
      const r = qs(`input[name="qpo-${si}"]:checked`, panel);
      if (r) r.checked = false;
    }
    update();
  }

  function nudge(el) {
    if (!el) return;
    el.classList.remove('is-nudged');
    void el.offsetWidth;
    el.classList.add('is-nudged');
    setTimeout(() => el.classList.remove('is-nudged'), 240);
  }

  async function go() {
    if (!S?.p || S.busy) return;
    const v = exact();
    if (!v) { // "Choose a size": shake the size row and put focus on its first size
      const row = qs('.qp__row--size', panel);
      nudge(row?.querySelector('.qp__pills'));
      row?.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
      return;
    }
    if (!v.available) return;
    S.busy = true;
    const btn = qs('.qp__go', panel);
    btn.setAttribute('aria-disabled', 'true');
    label('Adding…');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items: [{ id: v.id, quantity: 1 }] };
    if (cart?.getSectionsToRender) { body.sections = cart.getSectionsToRender().map((s) => s.id); body.sections_url = window.location.pathname; }
    const mine = S;
    try {
      const res = await fetch(`${window.routes?.cart_add_url || `${root}cart/add`}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'This could not be added.');
      if (S !== mine) return;
      btn.classList.add('is-added');
      label('Added', `${ICON_CHECK}<span>Added</span>`);
      // as product-form.js does (constants.js declares PUB_SUB_EVENTS with const, so it isn't on window)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-pick', productVariantId: v.id, cartData: json });
      const opener = S.opener;
      // the panel closes first, then the drawer opens with what's in the cart (Dawn's renderContents)
      setTimeout(() => {
        close(false, false);
        setTimeout(() => {
          if (!cart) { window.location.href = window.routes?.cart_url || `${root}cart`; return; }
          cart.setActiveElement?.(opener);
          if (cart.tagName === 'CART-DRAWER') cart.classList.remove('is-empty');
          if (json.sections && cart.renderContents) cart.renderContents({ id: v.id, sections: json.sections });
          else window.location.href = window.routes?.cart_url || `${root}cart`;
        }, mqReduce.matches ? 160 : mine.mode === 'sheet' ? 200 : 100);
      }, 420);
    } catch (err) {
      if (S !== mine) return;
      S.busy = false;
      btn.removeAttribute('aria-disabled');
      const e = qs('.qp__error', panel);
      e.textContent = err.message; e.hidden = false;
      update();
    }
  }

  // ---------- placing, closing, focus ----------
  function place() {
    const b = S.opener;
    if (!b || !b.isConnected) return;
    const r = b.getBoundingClientRect(), w = panel.offsetWidth, h = panel.offsetHeight, vw = document.documentElement.clientWidth;
    const up = window.innerHeight - r.bottom < h + 16 && r.top > h + 16;
    const left = Math.max(16, Math.min(r.right - w, vw - w - 16));
    // below the button, else above it; where neither fits, as near as the window allows, never under a sticky header
    const floor = (document.querySelector('.shopify-section-header-sticky')?.getBoundingClientRect().bottom || 0) + 8;
    const top = Math.max(floor, Math.min(up ? r.top - h - 10 : r.bottom + 10, window.innerHeight - h - 12));
    panel.style.left = `${left + window.scrollX}px`;
    panel.style.top = `${top + window.scrollY}px`;
    panel.style.transformOrigin = `${r.left + r.width / 2 - left}px ${Math.round(r.top + r.height / 2 - top)}px`;
  }

  function close(instant, refocus = true) {
    if (!S) return;
    const { mode, opener } = S;
    S = null;
    opener?.setAttribute('aria-expanded', 'false');
    panel.classList.remove('is-open', 'is-dragging');
    panel.style.transform = '';
    if (mode === 'sheet') {
      scrim.classList.remove('is-open');
      scrim.style.opacity = '';
      document.documentElement.classList.remove('qp-lock');
    }
    const done = () => { if (!S) { panel.hidden = true; scrim.hidden = true; panel.style.opacity = ''; } };
    if (instant) done(); else setTimeout(done, mode === 'sheet' ? 260 : 140);
    if (refocus && opener?.isConnected) opener.focus({ preventScroll: true });
  }

  function trap(e) {
    const items = [];
    const seen = new Set();
    qsa('button, a[href], input', panel).forEach((el) => {
      if (el.closest('[hidden]') || el.disabled) return;
      if (el.type === 'radio') { // a radio group is one stop: its checked one, else its first
        if (seen.has(el.name)) return;
        seen.add(el.name);
        const g = qsa(`input[name="${el.name}"]`, panel).filter((x) => !x.disabled);
        items.push(g.find((x) => x.checked) || g[0]);
      } else items.push(el);
    });
    const list = items.filter(Boolean);
    if (!list.length) return;
    const first = list[0], last = list[list.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // Drag the sheet down to close it: past 30% of its height, or flicked faster than 0.11 px/ms; otherwise it springs
  // back. Dragging up moves it less and less. A second finger is ignored once a drag has started.
  function drag() {
    let d = null;
    const damp = (x) => 22 * Math.log1p(x / 22);
    panel.addEventListener('pointerdown', (e) => {
      if (!S || S.mode !== 'sheet' || d || e.button > 0) return;
      if (e.target.closest('button, a, input, label')) return;
      if (!e.target.closest('.qp__grab, .qp__top')) return;
      d = { id: e.pointerId, y0: e.clientY, dy: 0, h: panel.offsetHeight, s: [[e.clientY, performance.now()]] };
      e.target.setPointerCapture?.(e.pointerId);
      panel.classList.add('is-dragging');
    });
    panel.addEventListener('pointermove', (e) => {
      if (!d || e.pointerId !== d.id) return;
      let dy = e.clientY - d.y0;
      if (dy < 0) dy = -damp(-dy);
      d.dy = dy;
      const t = performance.now();
      d.s.push([e.clientY, t]);
      while (d.s.length > 2 && t - d.s[0][1] > 100) d.s.shift();
      if (mqReduce.matches) panel.style.opacity = String(1 - Math.max(0, dy) / d.h);
      else panel.style.transform = `translateY(${dy}px)`;
      scrim.style.opacity = String(1 - Math.max(0, dy) / d.h);
    });
    const end = (e) => {
      if (!d || e.pointerId !== d.id) return;
      const [y0, t0] = d.s[0], [y1, t1] = d.s[d.s.length - 1];
      const v = t1 > t0 ? (y1 - y0) / (t1 - t0) : 0;
      const dismiss = d.dy > d.h * 0.3 || v > 0.11;
      panel.classList.remove('is-dragging');
      scrim.style.opacity = '';
      panel.style.opacity = '';
      d = null;
      panel.style.transform = '';
      if (dismiss) close();
    };
    panel.addEventListener('pointerup', end);
    panel.addEventListener('pointercancel', end);
  }

  // ---------- opening, and fetching ahead ----------
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-qp]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    if (S && S.opener === b) { close(); return; }
    open(b);
  });
  // a finger on Add, a focus on it, or the mouse resting on its card for a moment fetches the variants
  const ahead = (b) => b && load(b.dataset.qp).then((p) => preload(imgOf(p.variants[0]))).catch(() => {});
  document.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') ahead(e.target.closest?.('[data-qp]')); }, { passive: true });
  document.addEventListener('focusin', (e) => ahead(e.target.closest?.('[data-qp]')));
  let hoverT = 0;
  document.addEventListener('pointerover', (e) => {
    if (!mqMouse.matches || e.pointerType !== 'mouse') return;
    const card = e.target.closest?.('.card-wrapper');
    if (!card || card.contains(e.relatedTarget)) return;
    clearTimeout(hoverT);
    hoverT = setTimeout(() => ahead(qs('[data-qp]', card)), 120);
  });

  // ?lead=family (the home page's family section, a card showing the family): the link names a shirt in the picture's
  // color, so Dawn opens on that shirt; the family picture in that color then goes to the front of the gallery
  function leadFamily() {
    if (new URLSearchParams(location.search).get('lead') !== 'family') return;
    customElements.whenDefined('media-gallery').then(() => {
      const g = document.querySelector('media-gallery[id^="MediaGallery-"]');
      const item = g && [...g.querySelectorAll('[data-media-id]')].find((li) => (li.querySelector('img')?.alt || '').includes(', the whole family'));
      if (item && typeof g.setActiveMedia === 'function') g.setActiveMedia(item.dataset.mediaId, true);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', leadFamily); else leadFamily();

  function fromHash() {
    const m = location.hash.match(/qa=([^&]+)(?:&size=([^&]+))?/);
    if (!m) return;
    const b = document.querySelector(`[data-qp="${CSS.escape(decodeURIComponent(m[1]))}"]`);
    if (!b) return;
    b.scrollIntoView({ block: 'center' });
    setTimeout(() => open(b, { size: m[2] ? decodeURIComponent(m[2]) : undefined }), 50);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fromHash); else fromHash();
})();
